<!--
  DistributorOrdersList -- Distributor App cross-store Orders page.

  Card: Pricing & Splits -- Reporting & Insights (C)
  (https://trello.com/c/pSDstQD3).
  Pebble: Frontend: DistributorOrdersList + DistributorOrdersTable list page
  (replaces ComingSoon).

  Replaces the DistributorComingSoon frame at
  /distributor/reporting/orders. Fetches the actor's cross-store order list
  (newest-first, paginated) and renders it via DistributorOrdersTable. Scope
  is server-enforced -- an Owner sees every store under the distributor; a
  Builder is scoped to the stores they created -- and we surface a scope label
  so the actor knows what they are looking at.

  Backend contract -- LOCKED (PR #1818), `stores` + filters added additively:
    GET /api/reporting/distributor-orders
        ?limit=25&offset=0[&q=&store_id=&date_from=&date_to=]
      -> { scope: "owner"|"builder", total: <int>,
           orders: [ { id, order_number (nullable), store_id, store_name,
                       placed_at (iso8601), status, shopper_name,
                       total_dollars (number) } ],
           stores: [ { id, name } ] }

  `total` is the FILTERED count, so the range label stays honest while a
  filter is active.

  No Vuex: the page owns its own list + pagination state and calls the service
  directly (same pattern as PerformanceDashboard.vue).

  FILTERING (added because 1081 orders across 44 pages is unnavigable):
  a type-down search over order number / shopper name / shopper email, a
  store picker, a date range, and a page-size control.

  Three things the filter work had to get right:

  1. THE FILTER BAR LIVES OUTSIDE THE loading/error/empty BRANCHES. If it sat
     inside the "has rows" arm, filtering to zero results would unmount the
     controls and the user could not clear their own filter -- a dead end
     whose only exit is a page reload.

  2. loadOrders() NOW CARRIES A REQUEST-SEQUENCE GUARD. The previous version
     of this file documented an invariant: no guard was needed BECAUSE the
     pager is unmounted during a fetch (v-if="loading" wins), so a second
     request could not be started. The type-down breaks that premise by
     design -- the input must stay mounted and focused while a fetch is in
     flight -- so `reqId` is now load-bearing. Without it a slow request for
     "ab" can land after a fast one for "abc" and repaint the list with
     results for what the user typed three characters ago.

  3. EVERY FILTER CHANGE RESETS TO PAGE 1. Filtering to 3 results while
     offset=50 would otherwise render a blank page with no rows and no
     explanation.

  The page-size options stop at 100 because the backend clamps `limit` to
  [0, 100]. Offering more would silently return 100 and make the range label
  lie about what is on screen.
-->
<template>
  <section class="dord" data-testid="dist-orders-page">
    <header class="dord__head">
      <div class="dord__title-row">
        <h1 class="dord__title">Orders</h1>
        <!-- Staff order entry (card #566): moved here from the deleted
             /admin-buyer/dashboard. Gated on the same capability as its
             route AND on the SA switch add_new_order_enabled (card #645,
             default OFF; hidden while loading or unreadable); carries the
             store the list is filtered to. -->
        <router-link
          v-if="canCreateOrder && addNewOrderEnabled"
          :to="newOrderRoute"
          class="dord__add-order"
          data-testid="dist-orders-add-order"
        >
          Add New Order
        </router-link>
      </div>
      <p
        v-if="scope"
        class="dord__scope"
        data-testid="dist-orders-scope"
      >
        {{ scopeLabel }}
      </p>
    </header>

    <!-- FILTER BAR -- deliberately OUTSIDE the loading / error / empty
         branches below. Inside them, a filter matching nothing would unmount
         its own controls and strand the user with no way to clear it. -->
    <div class="dord__filters" data-testid="dist-orders-filters">
      <label class="dord__field dord__field--grow">
        <span class="dord__label">Search</span>
        <input
          type="search"
          class="dord__input"
          data-testid="dist-orders-search"
          placeholder="Order number, shopper name, or email"
          :value="search"
          @input="onSearchInput"
        />
      </label>

      <label class="dord__field">
        <span class="dord__label">Store</span>
        <select
          class="dord__input"
          data-testid="dist-orders-store-filter"
          :value="storeId"
          @change="onStoreChange"
        >
          <option value="">All stores</option>
          <option
            v-for="store in stores"
            :key="store.id"
            :value="String(store.id)"
          >
            {{ store.name }}
          </option>
        </select>
      </label>

      <label class="dord__field">
        <span class="dord__label">From</span>
        <input
          type="date"
          class="dord__input"
          data-testid="dist-orders-date-from"
          :value="dateFrom"
          @change="onDateFromChange"
        />
      </label>

      <label class="dord__field">
        <span class="dord__label">To</span>
        <input
          type="date"
          class="dord__input"
          data-testid="dist-orders-date-to"
          :value="dateTo"
          @change="onDateToChange"
        />
      </label>

      <label class="dord__field">
        <span class="dord__label">Per page</span>
        <select
          class="dord__input"
          data-testid="dist-orders-page-size"
          :value="String(limit)"
          @change="onPageSizeChange"
        >
          <option
            v-for="size in pageSizeOptions"
            :key="size"
            :value="String(size)"
          >
            {{ size }}
          </option>
        </select>
      </label>

      <button
        type="button"
        class="dord__clear"
        data-testid="dist-orders-clear-filters"
        :disabled="!hasActiveFilters"
        @click="clearFilters"
      >
        Clear filters
      </button>
    </div>

    <p
      v-if="loading"
      class="dord__loading"
      data-testid="dist-orders-loading"
      role="status"
    >
      Loading orders...
    </p>

    <p
      v-else-if="loadError"
      class="dord__error"
      data-testid="dist-orders-error"
      role="alert"
    >
      We could not load your orders. Please reload the page and try again.
    </p>

    <!-- Two DIFFERENT meanings for zero rows. Saying "No orders yet" while a
         filter is active is a lie that hides the filter from the person who
         set it. -->
    <p
      v-else-if="orders.length === 0"
      class="dord__empty"
      data-testid="dist-orders-empty"
      role="status"
    >
      {{
        hasActiveFilters
          ? "No orders match these filters. Try widening or clearing them."
          : "No orders yet. Orders placed in your stores will appear here."
      }}
    </p>

    <template v-else>
      <DistributorOrdersTable :orders="orders" :loading="loading" />

      <!-- The pager still lives inside the loading-gated v-else block, so
           Next/Prev are unmounted during a fetch. That is no longer the ONLY
           thing preventing an out-of-order fetch: loadOrders() now carries a
           reqId guard, added because the type-down input deliberately stays
           mounted during a fetch and can start a second request. The guard is
           the load-bearing protection now; this unmounting is belt-and-braces.
           (The previous version of this comment said no guard existed and
           explained why none was needed -- true then, false now.) -->
      <nav
        v-if="showPagination"
        class="dord__pager"
        data-testid="dist-orders-pager"
        aria-label="Orders pagination"
      >
        <button
          type="button"
          class="dord__pager-btn"
          data-testid="dist-orders-prev"
          :disabled="offset === 0"
          @click="prevPage"
        >
          Previous
        </button>
        <span class="dord__pager-range" data-testid="dist-orders-range">
          {{ rangeLabel }}
        </span>
        <button
          type="button"
          class="dord__pager-btn"
          data-testid="dist-orders-next"
          :disabled="!hasNextPage"
          @click="nextPage"
        >
          Next
        </button>
      </nav>
    </template>
  </section>
</template>

<script>
import { getDistributorOrders } from "@/services/distributorService";
import { usePermissions } from "@/composables/usePermissions";
import { useAddNewOrderEnabled } from "@/composables/useAddNewOrderEnabled";
import DistributorOrdersTable from "./DistributorOrdersTable.vue";

const PAGE_SIZE = 25;

// Capped at 100 because the backend clamps `limit` to [0, 100]. An option
// above the ceiling would silently return 100 rows while the range label
// claimed more -- a page-size control that lies about the page.
const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Type-down debounce. Long enough that an ordinary typing burst produces one
// request rather than one per keystroke; short enough to still feel live.
const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: "DistributorOrdersList",
  components: { DistributorOrdersTable },
  setup() {
    const { can } = usePermissions();
    // Card #645: fail-closed shared read of the SA switch (FALSE until loaded).
    const { addNewOrderEnabled } = useAddNewOrderEnabled();
    return { can, addNewOrderEnabled };
  },
  data() {
    return {
      scope: "",
      orders: [],
      stores: [],
      total: 0,
      limit: PAGE_SIZE,
      offset: 0,
      loading: true,
      loadError: false,
      // Filter state. `search` is the RAW input value; it is trimmed at
      // request time so a user can type a space mid-term without the list
      // blanking.
      search: "",
      storeId: "",
      dateFrom: "",
      dateTo: "",
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      // Monotonic request id. Only the response whose id still matches is
      // allowed to write to component state -- see loadOrders().
      reqId: 0,
      searchTimer: null,
    };
  },
  computed: {
    canCreateOrder() {
      return this.can("order.create");
    },
    newOrderRoute() {
      return {
        name: "distributor_reporting_order_new",
        query: this.storeId !== "" ? { store: this.storeId } : {},
      };
    },
    scopeLabel() {
      return this.scope === "owner"
        ? "All stores"
        : "Your stores";
    },
    hasNextPage() {
      // Derive purely from offset/limit/total (not the current page's row
      // count) so a short page can't misreport "next exists". A next page
      // exists whenever the window's end is short of the total.
      return this.offset + this.limit < this.total;
    },
    showPagination() {
      // Only worth showing a pager when there is more than one page of data.
      return this.total > this.limit;
    },
    rangeLabel() {
      if (this.orders.length === 0) return "0 of 0";
      const first = this.offset + 1;
      const last = this.offset + this.orders.length;
      return `${first}-${last} of ${this.total}`;
    },
    hasActiveFilters() {
      // Drives both the Clear button's disabled state and which empty-state
      // message is shown, so the two can never disagree about whether a
      // filter is active.
      return Boolean(
        this.search.trim() || this.storeId || this.dateFrom || this.dateTo,
      );
    },
  },
  created() {
    this.loadOrders();
  },
  beforeUnmount() {
    // A pending debounce would otherwise fire into an unmounted component.
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async loadOrders() {
      // REQUEST-SEQUENCE GUARD. The type-down input stays mounted during a
      // fetch (it must -- unmounting it would steal focus mid-word), so a
      // second request CAN start before the first resolves. Responses are
      // not ordered: a slow request for "ab" can land after a fast one for
      // "abc". Only the newest request may write state, so a stale response
      // -- resolved OR rejected -- is discarded instead of repainting the
      // list with results for a term the user has already moved past.
      const myReq = ++this.reqId;
      this.loading = true;
      this.loadError = false;
      try {
        const response = await getDistributorOrders(this.requestParams());
        if (myReq !== this.reqId) return;
        const data = response ? response.data : null;
        if (data) {
          this.scope = data.scope || "";
          this.total = typeof data.total === "number" ? data.total : 0;
          this.orders = Array.isArray(data.orders) ? data.orders : [];
          // The store list is scope-wide, not filter-narrowed, so it is safe
          // to refresh on every load without the options disappearing as
          // soon as one is picked.
          if (Array.isArray(data.stores)) this.stores = data.stores;
        } else {
          this.scope = "";
          this.total = 0;
          this.orders = [];
        }
      } catch (e) {
        // A stale REJECTION must not paint an error over a healthy newer
        // list, so the same guard applies on the failure path.
        if (myReq !== this.reqId) return;
        this.loadError = true;
      } finally {
        if (myReq === this.reqId) this.loading = false;
      }
    },

    // The ONE place filter state becomes request params. An empty filter is
    // OMITTED rather than sent blank, so "no filter" is expressed by absence
    // and there is no second convention for the backend to interpret. Keeping
    // this here (rather than in the service) means the service stays a thin
    // pass-through and only one layer decides what "empty" means.
    requestParams() {
      const params = { limit: this.limit, offset: this.offset };
      const term = this.search.trim();
      if (term) params.q = term;
      if (this.storeId !== "") params.store_id = Number(this.storeId);
      if (this.dateFrom) params.date_from = this.dateFrom;
      if (this.dateTo) params.date_to = this.dateTo;
      return params;
    },

    // --- Filter handlers ------------------------------------------------
    // Every one of these resets offset to 0 before re-fetching. Without it,
    // narrowing to 3 results while on page 3 renders a blank page.
    applyFilters() {
      this.offset = 0;
      this.loadOrders();
    },
    onSearchInput(event) {
      this.search = event.target.value;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.applyFilters();
      }, SEARCH_DEBOUNCE_MS);
    },
    onStoreChange(event) {
      // A select fires once per choice, so it needs no debounce.
      this.storeId = event.target.value;
      this.applyFilters();
    },
    onDateFromChange(event) {
      this.dateFrom = event.target.value;
      this.applyFilters();
    },
    onDateToChange(event) {
      this.dateTo = event.target.value;
      this.applyFilters();
    },
    onPageSizeChange(event) {
      const next = Number(event.target.value);
      if (!PAGE_SIZE_OPTIONS.includes(next)) return;
      this.limit = next;
      // Page 1 at the new size. Keeping the old offset would land the user
      // at an arbitrary point in the list, or past its end.
      this.applyFilters();
    },
    clearFilters() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.search = "";
      this.storeId = "";
      this.dateFrom = "";
      this.dateTo = "";
      this.applyFilters();
    },

    nextPage() {
      if (!this.hasNextPage) return;
      this.offset += this.limit;
      this.loadOrders();
    },
    prevPage() {
      if (this.offset === 0) return;
      this.offset = Math.max(0, this.offset - this.limit);
      this.loadOrders();
    },
  },
};
</script>

<style scoped>
.dord {
  max-width: 960px;
  margin: var(--tf-sp-8, 32px) auto;
  padding: 0 var(--tf-sp-4, 16px);
  color: var(--tf-fg-2, rgba(0, 0, 0, 0.75));
}
.dord__title {
  font-size: var(--tf-size-h2, 1.5rem);
  font-weight: 600;
  color: var(--tf-fg-1, rgba(0, 0, 0, 0.9));
  margin-bottom: var(--tf-sp-2, 8px);
}
.dord__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tf-sp-4, 16px);
  flex-wrap: wrap;
}
.dord__add-order {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: var(--tf-radius-pill, 9999px);
  background: var(--tf-primary, #119ae9);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
}
.dord__add-order:hover,
.dord__add-order:focus-visible {
  filter: brightness(0.95);
}
.dord__scope {
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  margin-bottom: var(--tf-sp-6, 24px);
}
.dord__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--tf-sp-3, 12px);
  margin-bottom: var(--tf-sp-4, 16px);
}
.dord__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.dord__field--grow {
  flex: 1 1 220px;
  min-width: 180px;
}
.dord__label {
  font-size: var(--tf-size-body-sm, 0.875rem);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
}
.dord__input {
  padding: 6px 10px;
  border: 1px solid var(--tf-border, rgba(0, 0, 0, 0.16));
  border-radius: var(--tf-radius-sm, 6px);
  background: var(--tf-surface, #fff);
  color: inherit;
  font: inherit;
}
.dord__clear {
  padding: 7px 14px;
  border: 1px solid var(--tf-border, rgba(0, 0, 0, 0.16));
  border-radius: var(--tf-radius-sm, 6px);
  background: var(--tf-surface, #fff);
  cursor: pointer;
}
.dord__clear:disabled {
  opacity: 0.45;
  cursor: default;
}
.dord__loading {
  font-style: italic;
}
.dord__error {
  color: var(--tf-error, #c62828);
  font-weight: 600;
}
.dord__empty {
  padding: var(--tf-sp-6, 24px) var(--tf-sp-4, 16px);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  font-style: italic;
}
.dord__pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--tf-sp-4, 16px);
  margin-top: var(--tf-sp-4, 16px);
}
.dord__pager-btn {
  padding: 6px 14px;
  border: 1px solid var(--tf-border, rgba(0, 0, 0, 0.16));
  border-radius: var(--tf-radius-sm, 6px);
  background: var(--tf-surface, #fff);
  cursor: pointer;
}
.dord__pager-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.dord__pager-range {
  font-size: var(--tf-size-body-sm, 0.875rem);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
}
</style>
